import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, Check, Sparkles, Building2, Users,
  Target, Phone, Zap, Calendar, MessageSquare, Trophy, X
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import Confetti from 'react-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Business type options
const BUSINESS_TYPES = [
  { id: 'real_estate', emoji: '🏠', name: 'Real Estate' },
  { id: 'insurance', emoji: '🛡️', name: 'Insurance' },
  { id: 'training', emoji: '📚', name: 'Training & Education' },
  { id: 'healthcare', emoji: '🏥', name: 'Healthcare' },
  { id: 'automobile', emoji: '🚗', name: 'Automobile' },
  { id: 'restaurant', emoji: '🍽️', name: 'Restaurant' },
  { id: 'ecommerce', emoji: '🛒', name: 'E-commerce' },
  { id: 'finance', emoji: '💰', name: 'Finance & Loans' },
  { id: 'travel', emoji: '✈️', name: 'Travel & Tourism' },
  { id: 'general', emoji: '💼', name: 'General Business' },
  { id: 'other', emoji: '➕', name: 'Other' },
];

// Team size options
const TEAM_SIZES = [
  { id: 'solo', label: 'Just me' },
  { id: 'small', label: '2-5 people' },
  { id: 'medium', label: '6-20 people' },
  { id: 'large', label: '20+ people' },
];

// Monthly leads options
const MONTHLY_LEADS = [
  { id: 'starter', label: 'Less than 50' },
  { id: 'growing', label: '50-200' },
  { id: 'scaling', label: '200-500' },
  { id: 'enterprise', label: '500+' },
];

// Goals options
const GOALS = [
  { id: 'capture', label: 'Capture more leads', icon: Users },
  { id: 'followup', label: 'Follow up faster', icon: Zap },
  { id: 'never_miss', label: 'Never miss a lead', icon: Target },
  { id: 'whatsapp', label: 'Automate WhatsApp', icon: MessageSquare },
  { id: 'voice_ai', label: 'AI voice calls', icon: Phone },
  { id: 'close_deals', label: 'Close more deals', icon: Trophy },
];

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_type: '',
    business_name: '',
    city: '',
    phone: '',
    team_size: '',
    monthly_leads: '',
    goals: [],
  });

  const handleBusinessTypeSelect = (typeId) => {
    setFormData({ ...formData, business_type: typeId });
  };

  const handleGoalToggle = (goalId) => {
    const goals = formData.goals.includes(goalId)
      ? formData.goals.filter(g => g !== goalId)
      : [...formData.goals, goalId];
    setFormData({ ...formData, goals });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setShowConfetti(true);
        setStep(4); // Go to completion screen
        setTimeout(() => {
          setShowConfetti(false);
        }, 5000);
      } else {
        throw new Error('Failed to save onboarding');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return !!formData.business_type;
      case 2:
        return formData.business_name && formData.city && formData.phone && formData.team_size && formData.monthly_leads;
      case 3:
        return formData.goals.length > 0;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (step === 3) {
      handleSubmit();
    } else {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  // Step content components
  const renderStep = () => {
    switch (step) {
      case 0: // Welcome
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center"
            >
              <Sparkles className="w-12 h-12 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-4">Welcome to AI Lead Mantra!</h1>
            <p className="text-lg text-emerald-200 mb-8">
              Let's set up your account in 2 minutes!
            </p>
            <Button
              onClick={nextStep}
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-lg font-semibold rounded-xl"
              data-testid="onboarding-start-btn"
            >
              Let's Get Started
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        );

      case 1: // Business Type
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">What's your business type?</h2>
            <p className="text-emerald-200 mb-6">Select the industry that best describes your business</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
              {BUSINESS_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => handleBusinessTypeSelect(type.id)}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                    formData.business_type === type.id
                      ? 'border-emerald-400 bg-emerald-900/50'
                      : 'border-emerald-700/50 hover:border-emerald-600 bg-emerald-900/20'
                  }`}
                  data-testid={`business-type-${type.id}`}
                >
                  <span className="text-3xl mb-2">{type.emoji}</span>
                  <span className="text-sm text-white text-center">{type.name}</span>
                  {formData.business_type === type.id && (
                    <Check className="w-4 h-4 text-emerald-400 mt-1" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 2: // Business Details
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Tell us about your business</h2>
            <p className="text-emerald-200 mb-4">This helps us personalize your experience</p>
            
            <div className="space-y-4 max-h-[380px] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm text-emerald-300 mb-1">
                  Business Name * 
                  {formData.business_name && <span className="text-emerald-400 ml-2">✓</span>}
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="e.g., Ajay Properties"
                  className={`w-full px-4 py-3 bg-emerald-900/30 border rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500 ${
                    formData.business_name ? 'border-emerald-500' : 'border-emerald-700/50'
                  }`}
                  data-testid="business-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-emerald-300 mb-1">
                  Your City *
                  {formData.city && <span className="text-emerald-400 ml-2">✓</span>}
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g., Hyderabad"
                  className={`w-full px-4 py-3 bg-emerald-900/30 border rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500 ${
                    formData.city ? 'border-emerald-500' : 'border-emerald-700/50'
                  }`}
                  data-testid="city-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-emerald-300 mb-1">
                  Your Phone *
                  {formData.phone && <span className="text-emerald-400 ml-2">✓</span>}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g., 9876543210"
                  className={`w-full px-4 py-3 bg-emerald-900/30 border rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500 ${
                    formData.phone ? 'border-emerald-500' : 'border-emerald-700/50'
                  }`}
                  data-testid="phone-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-emerald-300 mb-2">
                  Team Size *
                  {formData.team_size && <span className="text-emerald-400 ml-2">✓</span>}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {TEAM_SIZES.map(size => (
                    <button
                      key={size.id}
                      onClick={() => setFormData({ ...formData, team_size: size.id })}
                      className={`px-4 py-2 rounded-lg border transition-all ${
                        formData.team_size === size.id
                          ? 'border-emerald-400 bg-emerald-700/50 text-white'
                          : 'border-emerald-700/50 text-emerald-300 hover:border-emerald-600'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-emerald-300 mb-2">
                  Monthly Leads Expected *
                  {formData.monthly_leads && <span className="text-emerald-400 ml-2">✓</span>}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {MONTHLY_LEADS.map(leads => (
                    <button
                      key={leads.id}
                      onClick={() => setFormData({ ...formData, monthly_leads: leads.id })}
                      className={`px-4 py-2 rounded-lg border transition-all ${
                        formData.monthly_leads === leads.id
                          ? 'border-emerald-400 bg-emerald-700/50 text-white'
                          : 'border-emerald-700/50 text-emerald-300 hover:border-emerald-600'
                      }`}
                    >
                      {leads.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Goals
        return (
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">What are your goals?</h2>
            <p className="text-emerald-200 mb-6">Select all that apply</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {GOALS.map(goal => (
                <button
                  key={goal.id}
                  onClick={() => handleGoalToggle(goal.id)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    formData.goals.includes(goal.id)
                      ? 'border-emerald-400 bg-emerald-900/50'
                      : 'border-emerald-700/50 hover:border-emerald-600 bg-emerald-900/20'
                  }`}
                  data-testid={`goal-${goal.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    formData.goals.includes(goal.id) ? 'bg-emerald-500' : 'bg-emerald-800'
                  }`}>
                    <goal.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white font-medium">{goal.label}</span>
                  {formData.goals.includes(goal.id) && (
                    <Check className="w-5 h-5 text-emerald-400 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 4: // Completion
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center"
            >
              <Check className="w-12 h-12 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-4">🎉 Your account is ready!</h1>
            <p className="text-emerald-200 mb-6">Here's what we've configured for you:</p>
            
            <div className="bg-emerald-900/30 rounded-xl p-6 text-left mb-8 space-y-3">
              <div className="flex items-center gap-2 text-emerald-300">
                <Check className="w-5 h-5 text-emerald-400" />
                Business templates set for {BUSINESS_TYPES.find(t => t.id === formData.business_type)?.name}
              </div>
              <div className="flex items-center gap-2 text-emerald-300">
                <Check className="w-5 h-5 text-emerald-400" />
                AI scripts personalized
              </div>
              <div className="flex items-center gap-2 text-emerald-300">
                <Check className="w-5 h-5 text-emerald-400" />
                Pipeline stages configured
              </div>
              <div className="flex items-center gap-2 text-emerald-300">
                <Check className="w-5 h-5 text-emerald-400" />
                Lead form created
              </div>
              <div className="flex items-center gap-2 text-emerald-300">
                <Check className="w-5 h-5 text-emerald-400" />
                Follow-up sequences ready
              </div>
            </div>
            
            <Button
              onClick={onComplete}
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-lg font-semibold rounded-xl"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 z-50 flex items-center justify-center p-4">
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl bg-[#0a5c44] rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-emerald-600/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {[0, 1, 2, 3, 4].map(i => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all ${
                  i === step ? 'w-8 bg-emerald-400' : i < step ? 'w-2 bg-emerald-400' : 'w-2 bg-emerald-700'
                }`}
              />
            ))}
          </div>
          
          {step < 4 && (
            <button
              onClick={onSkip}
              className="text-emerald-300 hover:text-white text-sm flex items-center gap-1"
              data-testid="skip-onboarding-btn"
            >
              Skip
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Content */}
        <div className="p-6 min-h-[400px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
        
        {/* Footer Navigation */}
        {step > 0 && step < 4 && (
          <div className="p-4 border-t border-emerald-600/30">
            {/* Missing fields hint for step 2 */}
            {step === 2 && !canProceed() && (
              <p className="text-amber-400 text-sm mb-3 text-center">
                Please fill all required fields (*) above to continue
              </p>
            )}
            <div className="flex items-center justify-between">
              <Button
                onClick={prevStep}
                variant="ghost"
                className="text-emerald-300 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              
              <Button
                onClick={nextStep}
                disabled={!canProceed() || loading}
                className={`px-6 ${
                  canProceed() 
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-white' 
                    : 'bg-emerald-800/50 text-emerald-500/50 cursor-not-allowed'
                }`}
                data-testid="onboarding-next-btn"
                title={!canProceed() ? 'Please fill all required fields (*)' : ''}
              >
                {loading ? 'Saving...' : step === 3 ? 'Complete Setup' : 'Continue'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default OnboardingWizard;
